import { withMixpanel } from '@/analytics/withMixpanel'
import { AnalyticsBrowser, type MiddlewareFunction } from '@segment/analytics-next'
import { MARTECH_ENABLED_ENV, shouldSuppressMartech } from '@repo/martech-guard'
import type { App, Plugin } from 'vue'
import { type AnalyticsClient, setSegmentClient } from './analyticsClients'

export type SegmentOptions = {
  writeKey: string
}

// Associate session replays with Segment anonymous IDs:
// https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript/javascript-replay#segment-analyticsjs
const mixpanelMiddleware: MiddlewareFunction = ({ payload, next }) => {
  if (payload.obj.type === 'track' || payload.obj.type === 'page') {
    withMixpanel((mixpanel) => {
      const anonymousId = payload.obj.anonymousId

      mixpanel.register({
        $device_id: anonymousId,
        distinct_id: `$device:${anonymousId}`,
      })

      payload.obj.properties = {
        ...payload.obj.properties,
        ...mixpanel.get_session_recording_properties(),
      }
    })
  }

  next(payload)
}

export const segmentPlugin: Plugin = {
  async install(_: App, { writeKey }: SegmentOptions): Promise<void> {
    setSegmentClient(undefined)

    if (!writeKey) return

    // Skip loading Segment on local dev — set VITE_MARTECH_ENABLED=true in .env to test locally.
    if (shouldSuppressMartech({
      isLocalDevServer: import.meta.env.DEV,
      env: { [MARTECH_ENABLED_ENV]: import.meta.env.VITE_MARTECH_ENABLED },
    })) {
      return
    }

    try {
      const instance = AnalyticsBrowser.load({ writeKey })

      instance.addSourceMiddleware(mixpanelMiddleware)

      instance.catch(() => {
        console.error('Error loading segment')
        setSegmentClient(undefined)
      })

      setSegmentClient(instance as AnalyticsClient, instance)
    } catch (err) {
      console.error('Error loading segment: ', err)
    }
  },
}
